import {t} from 'i18next';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Box, useMediaQuery} from '@mui/material';
import {CaseContext} from '../../../../../redux/slice/aiReviewSliceV2';
import CaseContextTOC from './CaseContextTOC';
import CaseContextSectionGroup from './CaseContextSectionGroup';
import {
  CASE_CONTEXT_TOC_SECTIONS,
  CaseContextTocSectionId,
  getCaseContextSectionAnchorId,
} from './caseContextConfig';
import {useProtocolCaseContextState} from './useProtocolCaseContextState';
import {CaseContextMode} from './CaseContextFieldCard';
import BackToTopButton from '../back-to-top/BackToTopButton';
import './ProtocolCaseContext.scss';
import {RootState} from '../../../../../redux/store';
import {useSelector} from 'react-redux';

export interface ProtocolCaseContextProps {
  mode?: CaseContextMode;
  caseContext?: CaseContext;
  onCaseContextChange?: (caseContext: CaseContext) => void;
}

/** Quiet period after the last scroll event before treating programmatic scroll as done. */
const PROGRAMMATIC_SCROLL_SETTLE_MS = 120;
/** Safety fallback if scroll events stop unexpectedly. */
const PROGRAMMATIC_SCROLL_FALLBACK_MS = 1000;

const ProtocolCaseContext = ({
  mode = 'edit',
  caseContext: caseContextProp,
  onCaseContextChange,
}: ProtocolCaseContextProps) => {
  // Collapse Contents only strictly below 800px; keep open at the tablet breakpoint.
  const isBelowTablet = useMediaQuery('(max-width:799px)');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeSectionId, setActiveSectionId] =
    useState<CaseContextTocSectionId>('case-overview');
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const scrollFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const caseContextView = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.caseContext
  );
  const resolvedCaseContextProp =
    caseContextProp ?? (mode === 'view' ? caseContextView : undefined);

  const {caseContext, updateCaseContext} = useProtocolCaseContextState({
    caseContext: resolvedCaseContextProp,
    onCaseContextChange,
  });

  useEffect(() => {
    if (isBelowTablet) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isBelowTablet]);

  const clearProgrammaticScrollTimers = useCallback(() => {
    if (scrollSettleTimeoutRef.current) {
      clearTimeout(scrollSettleTimeoutRef.current);
      scrollSettleTimeoutRef.current = null;
    }
    if (scrollFallbackTimeoutRef.current) {
      clearTimeout(scrollFallbackTimeoutRef.current);
      scrollFallbackTimeoutRef.current = null;
    }
  }, []);

  const releaseProgrammaticScrollLock = useCallback(() => {
    clearProgrammaticScrollTimers();
    isProgrammaticScrollRef.current = false;
  }, [clearProgrammaticScrollTimers]);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const getActiveSectionFromScroll =
    useCallback((): CaseContextTocSectionId => {
      const panel = rightPanelRef.current;
      if (!panel) {
        return 'case-overview';
      }

      const panelRect = panel.getBoundingClientRect();
      const panelTop = panelRect.top;
      const panelHeight =
        panelRect.height || Math.max(panelRect.bottom - panelRect.top, 0);
      const sectionActivationLine =
        panelTop + (panelHeight > 0 ? panelHeight / 2 : 8);
      let currentId: CaseContextTocSectionId = CASE_CONTEXT_TOC_SECTIONS[0].id;
      // Activate a section once its header reaches the upper half of the panel.
      // This keeps the TOC in sync as the user enters a section instead of
      // waiting for its header to reach the panel top.
      CASE_CONTEXT_TOC_SECTIONS.forEach((section) => {
        const anchor = document.getElementById(
          getCaseContextSectionAnchorId(section.id)
        );
        if (!anchor) {
          return;
        }

        if (anchor.getBoundingClientRect().top <= sectionActivationLine) {
          currentId = section.id;
        }
      });
      return currentId;
    }, []);

  const handleSectionSelect = useCallback(
    (sectionId: CaseContextTocSectionId) => {
      // Pin the clicked section immediately and keep it pinned until scroll settles.
      setActiveSectionId(sectionId);
      isProgrammaticScrollRef.current = true;
      clearProgrammaticScrollTimers();

      const panel = rightPanelRef.current;
      const anchor = document.getElementById(
        getCaseContextSectionAnchorId(sectionId)
      );

      if (!panel || !anchor) {
        releaseProgrammaticScrollLock();
        return;
      }

      const panelRect = panel.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      panel.scrollTo({
        top: panel.scrollTop + (anchorRect.top - panelRect.top),
        behavior: 'smooth',
      });

      // Fallback in case scroll events never fire (already at position, etc.).
      scrollFallbackTimeoutRef.current = setTimeout(() => {
        releaseProgrammaticScrollLock();
      }, PROGRAMMATIC_SCROLL_FALLBACK_MS);
    },
    [clearProgrammaticScrollTimers, releaseProgrammaticScrollLock]
  );

  // Keep TOC highlight in sync when the user scrolls the right panel.
  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) {
      return undefined;
    }

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        // Wait until smooth scroll stops producing events, then unlock.
        // Do not update activeSectionId here — keep the clicked header.
        if (scrollSettleTimeoutRef.current) {
          clearTimeout(scrollSettleTimeoutRef.current);
        }
        scrollSettleTimeoutRef.current = setTimeout(() => {
          releaseProgrammaticScrollLock();
        }, PROGRAMMATIC_SCROLL_SETTLE_MS);
        return;
      }

      setActiveSectionId(getActiveSectionFromScroll());
    };

    panel.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      panel.removeEventListener('scroll', handleScroll);
      clearProgrammaticScrollTimers();
    };
  }, [
    clearProgrammaticScrollTimers,
    getActiveSectionFromScroll,
    releaseProgrammaticScrollLock,
  ]);

  return (
    <Box
      className="protocol-case-context"
      data-testid="protocol-case-context"
      role="region"
      aria-label={t('ai.review.newJobDetail.caseContext')}
    >
      <Box
        className={`case-context-left-panel ${isCollapsed ? 'case-context-left-panel--collapsed' : ''}`}
      >
        <CaseContextTOC
          activeSectionId={activeSectionId}
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
          onSectionSelect={handleSectionSelect}
        />
      </Box>
      <Box
        ref={rightPanelRef}
        className="case-context-right-panel"
        data-testid="case-context-right-panel"
      >
        {CASE_CONTEXT_TOC_SECTIONS.map((section) => (
          <CaseContextSectionGroup
            key={section.id}
            section={section}
            caseContext={caseContext}
            mode={mode}
            onUpdate={updateCaseContext}
          />
        ))}
        <BackToTopButton
          scrollContainerRef={rightPanelRef}
          onScrollToTop={() => handleSectionSelect('case-overview')}
        />
      </Box>
    </Box>
  );
};

export default ProtocolCaseContext;
